const STORAGE_KEY = 'salesworkx_contest_company_context'

/**
 * When a platform admin manages contests for a customer company,
 * store that company so Contests APIs send the correct orgId header.
 * Managers should leave this empty and use their own session companyId.
 */
export function setContestCompanyContext({ companyId, companyName } = {}) {
    if (companyId === null || companyId === undefined || companyId === '') {
        clearContestCompanyContext()
        return
    }

    const payload = {
        companyId: String(companyId).trim(),
        companyName: companyName || '',
        setAt: Date.now(),
    }

    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    } catch (err) {
        console.warn('Failed to persist contest company context', err)
    }
}

export function getContestCompanyContext() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed?.companyId) return null
        return {
            companyId: String(parsed.companyId),
            companyName: parsed.companyName || '',
        }
    } catch {
        return null
    }
}

export function clearContestCompanyContext() {
    try {
        sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        // ignore
    }
}

/**
 * Resolve where contest flows should return when leaving detail/edit pages.
 * Prefers explicit navigation state from company profile; falls back to session context.
 */
export function resolveContestReturnNavigation(locationState = {}) {
    const state = locationState || {}
    const activeTab = state.activeTab || 'Contests'

    if (state.companyId && state.fromCompanyProfile !== false) {
        return {
            path: '/company-profile',
            state: {
                companyId: state.companyId,
                companyName: state.companyName,
                activeTab,
            },
            label: 'Back to Customer',
        }
    }

    const context = getContestCompanyContext()
    if (context?.companyId) {
        return {
            path: '/company-profile',
            state: {
                companyId: context.companyId,
                companyName: context.companyName,
                activeTab: 'Contests',
            },
            label: 'Back to Customer',
        }
    }

    return {
        path: '/contests',
        state: state.companyId
            ? { companyId: state.companyId, companyName: state.companyName }
            : null,
        label: 'Back to Contests',
    }
}

export function navigateAfterContestFlow(navigate, locationState = {}) {
    const nav = resolveContestReturnNavigation(locationState)
    if (nav.state) {
        navigate(nav.path, { state: nav.state })
    } else {
        navigate(nav.path)
    }
}

export default {
    setContestCompanyContext,
    getContestCompanyContext,
    clearContestCompanyContext,
    resolveContestReturnNavigation,
    navigateAfterContestFlow,
}
